import React, { useState, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Draggable from 'react-draggable';
import { motion, AnimatePresence } from 'framer-motion';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  AboutIcon, ProjectsIcon, ContactIcon, ExperienceIcon, SettingsIcon, ChatIcon, WorkWithMeIcon,
  AboutIconMobile, ProjectsIconMobile, ContactIconMobile, ExperienceIconMobile, SettingsIconMobile, ChatIconMobile, WorkWithMeIconMobile
} from './IconSet';
import WaveBackground from './WaveBackground';
import HomeWindow from './HomeWindow';
import MobileInterface from './MobileInterface';
import ChatWindow from './ChatWindow';
import CaseStudyWindow from './CaseStudyWindow';
import CommandPalette from './CommandPalette';
import { WindowManagerContext } from './WindowManagerContext';
import { ThemeContext } from '../ThemeContext';
import { getCaseStudy } from '../content/caseStudies';

import { CHAT_WINDOW_FEATURE_FLAG, FREELANCE_FEATURE_FLAG } from '../constants/featureFlags';

// Clock component for Taskbar
const Clock = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{ 
      marginLeft: 'auto', 
      marginRight: '10px', 
      fontSize: '13px', 
      fontWeight: '500',
      fontFamily: 'monospace'
    }}>
      {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </div>
  );
};

// Desktop Background
const DesktopContainer = styled.div`
  min-height: 100vh;
  width: 100vw;
  /* Background removed to show WaveBackground */
  position: relative;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

// Desktop Icons
const IconColumn = styled.div`
  position: absolute;
  top: 20px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  /* Bound the height so icons wrap into a new column instead of
     overflowing past the taskbar (48px) when there are many of them. */
  max-height: calc(100vh - 20px - 48px - 20px);
  align-content: flex-start;
  gap: 30px 28px;
  z-index: 5;

  &.left { left: 30px; }
  /* Right column wraps leftward so extra columns stay on-screen. */
  &.right { right: 30px; flex-wrap: wrap-reverse; }

  @media (max-width: 768px) {
    display: none; // Hide desktop icons on mobile, use drawer
  }
`;

const DesktopIconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90px;
  cursor: pointer;
  padding: 10px;
  border-radius: 5px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'};
    transform: translateY(-2px);
  }

  svg {
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
  }

  @media (max-width: 768px) {
    width: auto;
    padding: 5px;
    
    svg {
      width: 48px;
      height: 48px;
    }
  }
`;

const IconLabel = styled.span`
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  /* Text-shadow carries legibility against the desktop; no pill box
     (the translucent background previously read as a rendering artifact). */
  text-shadow: ${props => props.theme === 'light'
    ? '0 1px 3px rgba(255,255,255,0.9), 0 0 2px rgba(255,255,255,0.9)'
    : '0 1px 3px rgba(0,0,0,0.7), 0 0 2px rgba(0,0,0,0.7)'};
  padding: 2px 6px;
`;

// Window Components
const Window = styled.div`
  background-color: ${props => props.theme === 'light' ? '#ffffff' : '#1e1e1e'};
  border-radius: ${props => props.maximized ? '0' : '6px'};
  box-shadow: ${props => props.active 
    ? '0 10px 40px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.1)' 
    : '0 5px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.05)'};
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease; /* Removed opacity transition as framer-motion handles it */
`;

const WindowHeader = styled.div`
  background-color: ${props => props.theme === 'light' ? '#e0e0e0' : '#2d2d2d'};
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: default; // Handle handles the drag
  border-bottom: 1px solid ${props => props.theme === 'light' ? '#ccc' : '#111'};
  user-select: none;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 13px;
  flex: 1;
`;

const WindowControls = styled.div`
  display: flex;
  gap: 8px;
`;

const ControlButton = styled.button`
  background: none;
  border: 1px solid transparent;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;
  font-size: 12px;
  transition: background-color 0.2s;
  padding: 0;
  
  &:hover {
    background-color: ${props => props.theme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'};
  }

  &.close:hover {
    background-color: #ff5f56;
    color: white;
  }
`;

const WindowContent = styled.div`
  padding: 0;
  overflow-y: auto;
  flex: 1;
  background-color: ${props => props.theme === 'light' ? '#fff' : '#1e1e1e'};
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  position: relative;
`;

// Taskbar
const Taskbar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 48px;
  background-color: ${props => props.theme === 'light' ? 'rgba(240, 240, 240, 0.9)' : 'rgba(30, 30, 30, 0.9)'};
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  padding: 0 10px;
  z-index: 10000;
  border-top: 1px solid ${props => props.theme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'};
  gap: 8px;
`;

const StartButton = styled.div`
  width: 36px;
  height: 36px;
  background-color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  color: ${props => props.theme === 'light' ? 'white' : '#333'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  margin-right: 10px;
  cursor: pointer;
`;

const TaskbarItem = styled.div`
  height: 36px;
  padding: 0 15px;
  background-color: ${props => props.active 
    ? (props.theme === 'light' ? '#e0e0e0' : '#444') 
    : 'transparent'};
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  min-width: 120px;
  max-width: 200px;
  border-bottom: ${props => props.active ? `2px solid ${props.theme === 'light' ? '#333' : '#aaa'}` : '2px solid transparent'};

  &:hover {
    background-color: ${props => props.theme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'};
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 13px;
    font-family: 'Monaco', 'Consolas', monospace;
  }
`;

// Theme toggle button (Desktop)
const DesktopThemeToggle = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: ${props => props.theme === 'light' ? '#fff' : '#333'};
  color: ${props => props.theme === 'light' ? '#333' : '#fff'};
  border: 1px solid ${props => props.theme === 'light' ? '#ddd' : '#444'};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const StartMenuContainer = styled(motion.div)`
  position: fixed;
  bottom: 54px; // 48px taskbar + 6px margin
  left: 10px;
  width: 220px;
  background-color: ${props => props.theme === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 30, 30, 0.95)'};
  backdrop-filter: blur(12px);
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  border: 1px solid ${props => props.theme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'};
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10001;
  transform-origin: bottom left;
`;

const StartMenuItem = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  transition: background-color 0.15s ease;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background-color: ${props => props.theme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${props => props.theme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'};
  margin: 4px 0;
`;

// Main component
const OSInterface = ({ children }) => {
  const { theme, setTheme } = useContext(ThemeContext);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const posthog = usePostHog();
  const isChatWindowEnabled = useFeatureFlagEnabled(CHAT_WINDOW_FEATURE_FLAG) === true;
  const isFreelanceEnabled = useFeatureFlagEnabled(FREELANCE_FEATURE_FLAG) === true;
  const windowRefs = useRef({});
  const windowOpenedAtRef = useRef({});
  
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [activeWindow, setActiveWindow] = useState('home');
  const [zIndexCounter, setZIndexCounter] = useState(100);
  const [windows, setWindows] = useState({
    // Desktop state stays intact while the mobile shell is active.
    'home': { 
      open: true,
      // Leave room for the desktop icons while keeping the welcome window on-screen.
      position: { x: Math.max(24, Math.min(260, window.innerWidth - 674)), y: 50 },
      zIndex: 10,
      minimized: false,
      maximized: false
    }
  });

  // Close start menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is on start button or inside menu
      if (e.target.closest('.start-button') || e.target.closest('.start-menu')) return;
      setStartMenuOpen(false);
    };
    
    if (startMenuOpen) {
      window.addEventListener('click', handleClickOutside);
    }
    return () => window.removeEventListener('click', handleClickOutside);
  }, [startMenuOpen]);
  
  useEffect(() => {
    if (isChatWindowEnabled) {
      return;
    }

    setWindows(prev => {
      if (!prev.chat?.open) {
        return prev;
      }

      return {
        ...prev,
        chat: {
          ...prev.chat,
          open: false,
          minimized: false,
        }
      };
    });

    setActiveWindow(prev => (prev === 'chat' ? 'home' : prev));
  }, [isChatWindowEnabled]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(prev => {
          if (!prev) posthog?.capture('palette_opened');
          return !prev;
        });
      }
      if (e.key === 'Escape' && paletteOpen) {
        setPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paletteOpen, posthog]);

  const toggleStartMenu = () => {
    posthog?.capture('start_menu_toggled', { open: !startMenuOpen });
    setStartMenuOpen(!startMenuOpen);
  };

  const handleStartMenuItemClick = (action, label) => {
    posthog?.capture('start_menu_item_clicked', { item: label });
    if (typeof action === 'function') action();
    else if (typeof action === 'string') openWindow(action, 'start_menu');
    setStartMenuOpen(false);
  };

  // Available sections (Files)
  const sections = [
    { id: 'about', title: 'about.md', icon: AboutIcon, mobileIcon: AboutIconMobile },
    { id: 'projects', title: 'projects.json', icon: ProjectsIcon, mobileIcon: ProjectsIconMobile },
    { id: 'experience', title: 'experience.log', icon: ExperienceIcon, mobileIcon: ExperienceIconMobile },
    { id: 'contact', title: 'contact.txt', icon: ContactIcon, mobileIcon: ContactIconMobile },
    { id: 'settings', title: 'settings.ini', icon: SettingsIcon, mobileIcon: SettingsIconMobile },
  ];

  if (isChatWindowEnabled) {
    sections.splice(4, 0, { id: 'chat', title: 'chat.local', icon: ChatIcon, mobileIcon: ChatIconMobile });
  }

  if (isFreelanceEnabled) {
    sections.unshift({ id: 'work-with-me', title: 'work-with-me.md', icon: WorkWithMeIcon, mobileIcon: WorkWithMeIconMobile });
  }

  // Additional fake files for right column
  const secondaryFiles = [
    // { id: 'settings', title: 'settings.ini', icon: () => <span style={{fontSize: '24px'}}>⚙️</span> },
    // { id: 'trash', title: 'Trash', icon: () => <span style={{fontSize: '24px'}}>🗑️</span> }
  ];

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    posthog?.capture('theme_toggled', { theme: newTheme });
    setTheme(newTheme);
  };

  const openWindow = (sectionId, source = 'unknown') => {
    if (sectionId === 'chat' && !isChatWindowEnabled) {
      return;
    }
    if (sectionId === 'work-with-me' && !isFreelanceEnabled) {
      return;
    }

    const newZIndex = zIndexCounter + 1;
    setZIndexCounter(newZIndex);
    const wasAlreadyOpen = windows[sectionId]?.open;

    setWindows(prev => {
      // If already open, just bring to front and unminimize
      if (prev[sectionId]?.open) {
        return {
          ...prev,
          [sectionId]: {
            ...prev[sectionId],
            zIndex: newZIndex,
            minimized: false
          }
        };
      }

      // If opening new window
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = 650;
      const height = 500;

      // Randomish position
      const offset = Object.keys(prev).length * 30;
      const x = Math.min(viewportWidth - width - 50, 100 + offset);
      const y = Math.min(viewportHeight - height - 100, 50 + offset);

      return {
        ...prev,
        [sectionId]: {
          open: true,
          position: { x, y },
          zIndex: newZIndex,
          minimized: false,
          maximized: isMobile ? true : false, // Force maximized on mobile
        }
      };
    });

    if (!wasAlreadyOpen) {
      windowOpenedAtRef.current[sectionId] = Date.now();
      const isCaseStudy = sectionId.startsWith('case-study:');
      const virtualPath = isCaseStudy
        ? `/window/case-study/${sectionId.slice('case-study:'.length)}`
        : `/window/${sectionId}`;
      posthog?.capture('window_opened', { window: sectionId, source });
      // Virtual pageview so funnels in PostHog treat each window as a "page"
      posthog?.capture('$pageview', {
        $current_url: typeof window !== 'undefined' ? `${window.location.origin}${virtualPath}` : virtualPath,
        virtual: true,
        window: sectionId,
        source,
      });
    } else {
      posthog?.capture('window_refocused', { window: sectionId, source });
    }
    setActiveWindow(sectionId);
  };

  const closeWindow = (sectionId) => {
    const openedAt = windowOpenedAtRef.current[sectionId];
    const durationMs = openedAt ? Date.now() - openedAt : null;
    posthog?.capture('window_closed', {
      window: sectionId,
      duration_ms: durationMs,
      duration_seconds: durationMs != null ? Math.round(durationMs / 1000) : null,
    });
    delete windowOpenedAtRef.current[sectionId];
    setWindows(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], open: false }
    }));
  };

  const minimizeWindow = (sectionId, e) => {
    e.stopPropagation();
    posthog?.capture('window_minimized', { window: sectionId });
    setWindows(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], minimized: true }
    }));
    setActiveWindow(null); // Deselect
  };

  const maximizeWindow = (sectionId, e) => {
    e.stopPropagation();
    // Disable unmaximize on mobile
    if (isMobile) return;

    const newMaximized = !windows[sectionId]?.maximized;
    posthog?.capture('window_maximized', { window: sectionId, maximized: newMaximized });
    setWindows(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], maximized: !prev[sectionId].maximized }
    }));
  };

  const handleWindowClick = (sectionId) => {
    const newZIndex = zIndexCounter + 1;
    setZIndexCounter(newZIndex);
    setActiveWindow(sectionId);
    setWindows(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], zIndex: newZIndex }
    }));
  };

  const handleTaskbarClick = (sectionId) => {
    const win = windows[sectionId];
    if (win.minimized) {
      // Restore
      posthog?.capture('taskbar_clicked', { window: sectionId, action: 'restore' });
      const newZIndex = zIndexCounter + 1;
      setZIndexCounter(newZIndex);
      setWindows(prev => ({
        ...prev,
        [sectionId]: { ...prev[sectionId], minimized: false, zIndex: newZIndex }
      }));
      setActiveWindow(sectionId);
    } else if (activeWindow === sectionId) {
      // Minimize if already active
      posthog?.capture('taskbar_clicked', { window: sectionId, action: 'minimize' });
      minimizeWindow(sectionId, { stopPropagation: () => {} });
    } else {
      // Bring to front
      posthog?.capture('taskbar_clicked', { window: sectionId, action: 'focus' });
      handleWindowClick(sectionId);
    }
  };

  const findComponentForSection = (sectionId) => {
    if (sectionId === 'home') {
      return <HomeWindow theme={theme} onOpenSection={openWindow} showChatAction={isChatWindowEnabled} />;
    }
    if (sectionId === 'chat') {
      if (!isChatWindowEnabled) {
        return null;
      }
      return <ChatWindow theme={theme} />;
    }
    if (sectionId.startsWith('case-study:')) {
      const slug = sectionId.slice('case-study:'.length);
      return <CaseStudyWindow slug={slug} />;
    }
    return React.Children.toArray(children).find(child => child.props.id === sectionId);
  };

  // Render Desktop Icons
  const renderDesktopIcon = (item) => {
    const Icon = item.icon;
    const source = isMobile ? 'mobile_grid' : 'desktop_icon';
    return (
      <DesktopIconWrapper
        key={item.id}
        onClick={() => {
          posthog?.capture('desktop_icon_clicked', { window: item.id, source });
          openWindow(item.id, source);
        }}
        theme={theme}
      >
        <Icon />
        <IconLabel theme={theme}>{item.title}</IconLabel>
      </DesktopIconWrapper>
    );
  };

  const windowManagerValue = {
    openWindow,
    openCaseStudy: (slug, source = 'unknown') => openWindow(`case-study:${slug}`, source),
    openPalette: () => setPaletteOpen(true),
  };

  if (isMobile) {
    return <MobileInterface sections={sections} renderSection={findComponentForSection} />;
  }

  return (
    <WindowManagerContext.Provider value={windowManagerValue}>
    <DesktopContainer theme={theme}>
      <WaveBackground />
      <DesktopThemeToggle onClick={toggleTheme} theme={theme}>
        {theme === 'light' ? '🌙' : '☀️'}
      </DesktopThemeToggle>

      {/* Desktop Icons */}
      {!isMobile && (
        <>
          <IconColumn className="left">
            {sections.map(renderDesktopIcon)}
          </IconColumn>
          <IconColumn className="right">
            {secondaryFiles.map(renderDesktopIcon)}
          </IconColumn>
        </>
      )}

      {/* Windows */}
      <AnimatePresence>
        {Object.keys(windows).map(id => {
          const winState = windows[id];
          if (!winState.open) return null;
          
          // Determine title and content
          let title = 'Document';
          const section = sections.find(s => s.id === id);
          if (id === 'home') title = 'README.md';
          else if (id.startsWith('case-study:')) {
            const slug = id.slice('case-study:'.length);
            const cs = getCaseStudy(slug);
            title = cs ? `${slug}.md` : `${slug}.md`;
          }
          else if (section) title = section.title;

          const content = findComponentForSection(id);
          // Force maximized logic on mobile
          const isMaximized = winState.maximized || isMobile;
          const isMinimized = winState.minimized;
          
          if (!windowRefs.current[id]) {
            windowRefs.current[id] = React.createRef();
          }

          return (
            <motion.div
              key={id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: winState.zIndex
              }}
            >
              <Draggable
                handle=".window-handle"
                defaultPosition={winState.position}
                position={isMaximized ? {x: 0, y: 0} : null}
                onStart={() => handleWindowClick(id)}
                disabled={isMaximized} // Disabled on mobile because isMaximized is forced true
                bounds="parent"
                nodeRef={windowRefs.current[id]} 
              >
                <div 
                  ref={windowRefs.current[id]}
                  style={{ position: 'absolute', pointerEvents: 'auto', display: 'flex', flexDirection: 'column' }}
                >
                  <motion.div
                    initial={false}
                    animate={{
                      width: isMaximized ? '100vw' : '650px',
                      height: isMaximized ? 'calc(100vh - 48px)' : 'auto',
                      maxHeight: isMaximized ? 'calc(100vh - 48px)' : '80vh',
                      scale: isMinimized ? 0 : 1,
                      opacity: isMinimized ? 0 : 1,
                      y: isMinimized ? 200 : 0
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    style={{ 
                      transformOrigin: 'bottom left',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Window 
                      theme={theme}
                      maximized={isMaximized}
                      active={activeWindow === id}
                      onClick={() => handleWindowClick(id)}
                    >
                      <WindowHeader theme={theme} className="window-handle" onDoubleClick={(e) => maximizeWindow(id, e)}>
                        <TitleBar>
                          <span style={{ fontSize: '14px' }}>{id === 'chat' ? '💬' : id.startsWith('case-study:') ? '📝' : '📄'}</span>
                          {title}
                        </TitleBar>
                        <WindowControls onMouseDown={(e) => e.stopPropagation()}>
                          <ControlButton theme={theme} onClick={(e) => minimizeWindow(id, e)} title="Minimize">─</ControlButton>
                          {!isMobile && (
                            <ControlButton theme={theme} onClick={(e) => maximizeWindow(id, e)} title="Maximize">□</ControlButton>
                          )}
                          <ControlButton theme={theme} className="close" onClick={(e) => { e.stopPropagation(); closeWindow(id); }} title="Close">×</ControlButton>
                        </WindowControls>
                      </WindowHeader>
                      <WindowContent theme={theme}>
                        {content}
                      </WindowContent>
                    </Window>
                  </motion.div>
                </div>
              </Draggable>
            </motion.div>
          );
        })}
      </AnimatePresence>

      <AnimatePresence>
        {startMenuOpen && (
          <StartMenuContainer
            theme={theme}
            className="start-menu"
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
          >
             {/* Readme/Home */}
             <StartMenuItem theme={theme} onClick={() => handleStartMenuItemClick('home', 'home')}>
                <span style={{fontSize: '18px'}}>📄</span> README.md
             </StartMenuItem>

             <Divider theme={theme} />

             {sections.map(section => {
               const Icon = section.mobileIcon;
               return (
                <StartMenuItem key={section.id} theme={theme} onClick={() => handleStartMenuItemClick(section.id, section.id)}>
                   <Icon />
                   {section.title}
                </StartMenuItem>
               );
             })}

             <Divider theme={theme} />

             <StartMenuItem theme={theme} onClick={() => handleStartMenuItemClick(() => window.location.reload(), 'restart')}>
                <span style={{fontSize: '18px'}}>🔄</span> Restart
             </StartMenuItem>
             
          </StartMenuContainer>
        )}
      </AnimatePresence>

      {/* Taskbar */}
      <Taskbar theme={theme}>
        <StartButton theme={theme} onClick={toggleStartMenu} className="start-button">❖</StartButton>
        {Object.keys(windows).filter(id => windows[id].open).map(id => {
          const section = sections.find(s => s.id === id);
          let title = id === 'home' ? 'README.md' : (section ? section.title : id);
          if (id.startsWith('case-study:')) {
            const slug = id.slice('case-study:'.length);
            const cs = getCaseStudy(slug);
            title = cs ? cs.title : slug;
          }
          const taskbarIcon = id === 'chat' ? '💬' : id.startsWith('case-study:') ? '📝' : '📄';
          
          return (
            <TaskbarItem 
              key={id} 
              active={activeWindow === id && !windows[id].minimized}
              onClick={() => handleTaskbarClick(id)}
              theme={theme}
            >
              <span>{taskbarIcon} {title}</span>
            </TaskbarItem>
                      );
                    })}
                  <Clock />
                </Taskbar>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        openWindow={openWindow}
      />
    </DesktopContainer>
    </WindowManagerContext.Provider>
  );
};

export default OSInterface; 
